import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface SchoolYear {
  id: string;
  name?: string;
  label?: string;
  startDate?: string | number[];
  endDate?: string | number[];
}

interface Props {
  schoolYears: SchoolYear[];
  selectedSchoolYearId: string;
  onChange: (id: string) => void;
  label?: string;
}

type SchoolYearBucket = "active" | "future" | "previous";

const parseSchoolYearDate = (value?: string | number[]) => {
  if (!value) return null;

  if (Array.isArray(value)) {
    if (value.length < 3) return null;

    return new Date(value[0], value[1] - 1, value[2]);
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (match) {
    return new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

export const getSchoolYearLabel = (schoolYear: SchoolYear) => {
  if (schoolYear.name) return schoolYear.name;
  if (schoolYear.label) return schoolYear.label;

  const start = parseSchoolYearDate(schoolYear.startDate);
  const end = parseSchoolYearDate(schoolYear.endDate);

  if (start && end) {
    return `${start.getFullYear()} - ${end.getFullYear()}`;
  }

  return "Unknown school year";
};

export const getSchoolYearRange = (schoolYear: SchoolYear) => {
  const startDate = parseSchoolYearDate(schoolYear.startDate);
  const endDate = parseSchoolYearDate(schoolYear.endDate);

  if (startDate && endDate) {
    return {
      start: startDate,
      end: endDate,
    };
  }

  const label = schoolYear.name ?? schoolYear.label ?? "";
  const match = label.match(/(\d{4})\s*[-/]\s*(\d{4})/);

  if (!match) return null;

  const startYear = Number(match[1]);
  const endYear = Number(match[2]);

  return {
    start: new Date(startYear, 7, 1),
    end: new Date(endYear, 6, 30, 23, 59, 59),
  };
};

export const getSchoolYearBucket = (
  schoolYear: SchoolYear
): SchoolYearBucket => {
  const range = getSchoolYearRange(schoolYear);
  const now = new Date();

  if (!range) return "future";

  if (now >= range.start && now <= range.end) {
    return "active";
  }

  if (range.start > now) {
    return "future";
  }

  return "previous";
};

export const getInitialSchoolYearId = (
  years: SchoolYear[]
): string => {
  const active = years.find(
    (year) => getSchoolYearBucket(year) === "active"
  );

  if (active) {
    return active.id;
  }

  const future = [...years]
    .filter((year) => getSchoolYearBucket(year) === "future")
    .sort((a, b) => {
      const aStart =
        getSchoolYearRange(a)?.start.getTime() ?? 0;
      const bStart =
        getSchoolYearRange(b)?.start.getTime() ?? 0;

      return aStart - bStart;
    });

  if (future.length > 0) {
    return future[0].id;
  }

  return years[0]?.id ?? "";
};

const sortAscending = (years: SchoolYear[]) =>
  [...years].sort((a, b) => {
    const aStart =
      getSchoolYearRange(a)?.start.getTime() ?? 0;
    const bStart =
      getSchoolYearRange(b)?.start.getTime() ?? 0;

    return aStart - bStart;
  });

const SchoolYearDropdown: React.FC<Props> = ({
  schoolYears,
  selectedSchoolYearId,
  onChange,
  label,
}) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedSchoolYear = schoolYears.find(
    (year) => year.id === selectedSchoolYearId
  );

  const groupedYears = useMemo(() => {
    return {
      active: sortAscending(
        schoolYears.filter(
          (year) => getSchoolYearBucket(year) === "active"
        )
      ),
      future: sortAscending(
        schoolYears.filter(
          (year) => getSchoolYearBucket(year) === "future"
        )
      ),
      previous: sortAscending(
        schoolYears.filter(
          (year) => getSchoolYearBucket(year) === "previous"
        )
      ).reverse(),
    };
  }, [schoolYears]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () =>
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
  }, []);

  const renderGroup = (
    title: string,
    years: SchoolYear[]
  ) => {
    if (years.length === 0) return null;

    return (
      <div className="py-1">
        <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
          {title}
        </div>

        {years.map((year) => {
          const selected =
            year.id === selectedSchoolYearId;

          const bucket = getSchoolYearBucket(year);

          return (
            <button
              key={year.id}
              type="button"
              onClick={() => {
                onChange(year.id);
                setOpen(false);
              }}
              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                selected
                  ? "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                  : "text-gray-800 dark:text-gray-200"
              }`}
            >
              <span className="font-medium">
                {getSchoolYearLabel(year)}
              </span>

              {bucket === "active" && (
                <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-green-200 dark:bg-green-900/20 dark:text-green-300 dark:ring-green-800">
                  {t(
                    "schoolYears.active",
                    "Current"
                  )}
                </span>
              )}

              {selected && bucket !== "active" && (
                <span className="text-xs text-blue-600 dark:text-blue-300">
                  {t(
                    "schoolYears.selected",
                    "Selected"
                  )}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div
      ref={dropdownRef}
      className="relative min-w-[260px]"
    >
      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label ??
          t("schoolYears.label", "School year")}
      </label>

      <button
        type="button"
        aria-label={label ?? t("schoolYears.label", "School year")}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-xl border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-900 shadow-sm transition hover:bg-gray-50 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
      >
        <span className="flex items-center gap-2">
          {selectedSchoolYear
            ? getSchoolYearLabel(selectedSchoolYear)
            : "-"}

          {selectedSchoolYear &&
            getSchoolYearBucket(selectedSchoolYear) ===
              "active" && (
              <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700 ring-1 ring-green-200 dark:bg-green-900/20 dark:text-green-300 dark:ring-green-800">
                {t(
                  "schoolYears.active",
                  "Current"
                )}
              </span>
            )}
        </span>

        <span
          className={`transition-transform ${
            open ? "rotate-180" : ""
          }`}
        >
          ▼
        </span>
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 max-h-96 w-full overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-xl dark:border-gray-700 dark:bg-gray-800">
          {renderGroup(
            t(
              "schoolYears.groups.active",
              "Current school year"
            ),
            groupedYears.active
          )}

          {renderGroup(
            t(
              "schoolYears.groups.future",
              "Future school years"
            ),
            groupedYears.future
          )}

          {renderGroup(
            t(
              "schoolYears.groups.previous",
              "Previous school years"
            ),
            groupedYears.previous
          )}
        </div>
      )}
    </div>
  );
};

export default SchoolYearDropdown;
